import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  FiClipboard, FiRepeat, FiMessageSquare, FiSearch,
  FiChevronLeft, FiChevronRight, FiArrowLeft, FiArrowRight,
  FiCopy, FiCheck, FiUpload, FiFileText, FiCalendar,
  FiDownload, FiShare2, FiFilter, FiX, FiTrash2, FiRefreshCw,
} from 'react-icons/fi';
import '../../styles/Artifacts.css';
import '../../styles/MomentumCommand.css';
import { useArtifactsStats } from '../../context/ArtifactsStatsContext';

const LIMIT = 12;


type Tab = 'tasks' | 'routines' | 'chats' | 'uploads';


const TAB_FLOW: Record<Tab, string> = {
  tasks:    'tasks',
  routines: 'routines',
  chats:    'chats',
  uploads:  'upload',   
};

interface ArtefactItem {
  id:              string;
  filename:        string;
  session_id:      string | null;
  routine_id:      string | null;
  routine_run_id:  number | null;
  task_id:         string | null;
  created_at:      string;
  app_url:         string;
  download_url:    string;
  version_number?: number;
  has_versions?:   boolean;
  


  conversation_id?: string | null;
}

interface Summary {
  total:    number;
  routines: number;
  tasks:    number;
  chats:    number;
  uploads:  number;
}


const SOURCE_CFG: Record<Tab, { label: string; color: string; initials: string; icon: React.ReactNode }> = {
  tasks:    { label: 'Command', color: '#8b5cf6', initials: 'CMD', icon: <FiClipboard size={13} /> },
  routines: { label: 'Routine', color: '#00b0d3', initials: 'RT', icon: <FiRepeat size={13} /> },
  chats:    { label: 'Chat',    color: '#22c55e', initials: 'CH', icon: <FiMessageSquare size={13} /> },
  uploads:  { label: 'Upload',  color: '#f59e0b', initials: 'UP', icon: <FiUpload size={13} /> },
};


const EMPTY_ICON: Record<Tab, React.ReactNode> = {
  tasks:    <FiClipboard size={30} />,
  routines: <FiRepeat size={30} />,
  chats:    <FiMessageSquare size={30} />,
  uploads:  <FiUpload size={30} />,
};
const EMPTY_DESCRIPTIONS: Record<Tab, string> = {
  tasks:    'Files and reports created by command agents will appear here.',
  routines: 'Documents generated by your scheduled routines will appear here.',
  chats:    'Files shared or generated during chat sessions will appear here.',
  uploads:  'Files you upload for processing or analysis will appear here.',
};


function timeAgo(d?: string): string {
  if (!d) return '';
  const diff = Date.now() - new Date(d).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const days = Math.floor(h / 24);
  if (days < 30) return `${days}d ago`;
  return `${Math.floor(days / 30)}mo ago`;
}

function fmtDateTime(d?: string): string {
  if (!d) return '';
  try {
    return new Date(d).toLocaleString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: 'numeric', minute: '2-digit',
    });
  } catch { return d; }
}

function formatFilename(filename: string): string {
  const noExt = filename.replace(/\.[^.]+$/, '');
  return noExt
    .replace(/[-_]/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase());
}

function fileExtension(filename: string): string {
  const m = filename.match(/\.([^.]+)$/);
  return m ? m[1].toUpperCase() : 'FILE';
}

function authHeaders(): Record<string, string> {
  const token = localStorage.getItem('authToken') || '';
  return { Authorization: `Bearer ${token}` };
}








function triggerDownload(url: string, filename: string) {
  fetch(url, { headers: authHeaders() })
    .then(r => {
      if (!r.ok) throw new Error('Download failed');
      return r.blob();
    })
    .then(blob => {
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = filename || 'download';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(blobUrl), 10000);
    })
    .catch(() => {
      
      
      const a = document.createElement('a');
      a.href = url;
      a.rel = 'noopener noreferrer';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    });
}


const EXT_COLOR: Record<string, string> = {
  pdf:  '#ef4444',
  xlsx: '#22c55e', xls: '#22c55e', xlsm: '#22c55e',
  csv:  '#10b981', tsv: '#10b981',
  docx: '#3b82f6', doc: '#3b82f6',
  pptx: '#f97316', ppt: '#f97316',
  html: '#f59e0b', htm: '#f59e0b',
  md:   '#a78bfa', txt: '#6366f1',
  json: '#06b6d4',
};

function extColor(filename: string): string {
  const ext = filename.split('.').pop()?.toLowerCase() ?? '';
  return EXT_COLOR[ext] ?? '#8b5cf6';
}


type DatePreset = 'today' | 'week' | 'month';
const DATE_PRESETS: Array<{ key: DatePreset; label: string }> = [
  { key: 'today', label: 'Today' },
  { key: 'week',  label: 'Last Week' },
  { key: 'month', label: 'Last Month' },
];
function fmtDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}
function presetDates(preset: DatePreset): { from: string; to: string } {
  const today = new Date();
  const from  = new Date(today);
  if (preset === 'week') from.setDate(today.getDate() - 6);
  else if (preset === 'month') from.setDate(today.getDate() - 29);
  return { from: fmtDate(from), to: fmtDate(today) };
}


const CardSkeleton: React.FC = () => (
  <div className="art-card" style={{ pointerEvents: 'none', cursor: 'default' }}>
    <div className="art-card__preview" style={{ minHeight: 130, alignItems: 'center', justifyContent: 'center' }}>
      <div className="art-skeleton" style={{ width: 52, height: 52, borderRadius: 14 }} />
    </div>
    <div className="art-card__body">
      <div className="art-skeleton" style={{ height: 14, width: '70%', marginBottom: 6 }} />
      <div className="art-skeleton" style={{ height: 11, width: '48%', marginBottom: 10 }} />
      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
        <div className="art-skeleton" style={{ width: 20, height: 20, borderRadius: 6 }} />
        <div className="art-skeleton" style={{ height: 11, width: 46 }} />
        <div className="art-skeleton" style={{ height: 11, width: 38 }} />
      </div>
    </div>
  </div>
);


const DetailPageSkeleton: React.FC = () => (
  <div className="art-viewer">
    <div className="art-viewer__toolbar">
      <div className="art-skeleton" style={{ width: 32, height: 32, borderRadius: 8, flexShrink: 0 }} />
      <div className="art-skeleton" style={{ width: 16, height: 16, borderRadius: 4, flexShrink: 0 }} />
      <div className="art-skeleton" style={{ height: 15, width: 220 }} />
      <div style={{ flex: 1 }} />
      <div style={{ display: 'flex', gap: 6 }}>
        <div className="art-skeleton" style={{ height: 30, width: 105, borderRadius: 7 }} />
        <div className="art-skeleton" style={{ height: 30, width: 88, borderRadius: 7 }} />
        <div className="art-skeleton" style={{ height: 30, width: 92, borderRadius: 7 }} />
      </div>
    </div>
    <div className="art-viewer__pane" style={{ minHeight: 480 }}>
      <div className="art-viewer__sidebar">
        {([36, '85%', 54, 60] as const).map((w, i) => (
          <div key={i} className="art-viewer__sidebar-section">
            <div className="art-skeleton" style={{ height: 8, width: 38, marginBottom: 7, borderRadius: 3 }} />
            <div className="art-skeleton" style={{ height: 13, width: w }} />
          </div>
        ))}
      </div>
      <div className="art-viewer__document">
        <div className="art-skel-doc">
          <div className="art-skeleton" style={{ height: 22, width: '52%', marginBottom: 22 }} />
          {[90, 82, 75, 88, 60, 78].map((w, i) => (
            <div key={i} className="art-skeleton" style={{ height: 13, width: `${w}%`, marginBottom: 11 }} />
          ))}
          <div style={{ height: 12 }} />
          <div className="art-skeleton" style={{ height: 120, borderRadius: 10, marginBottom: 18 }} />
          {[85, 68, 80, 58].map((w, i) => (
            <div key={i} className="art-skeleton" style={{ height: 13, width: `${w}%`, marginBottom: 11 }} />
          ))}
        </div>
      </div>
    </div>
  </div>
);


const DocLoadingSkeleton: React.FC = () => (
  <div className="art-skel-doc">
    <div className="art-skeleton" style={{ height: 20, width: '50%', marginBottom: 20 }} />
    {[92, 80, 74, 86, 62, 76].map((w, i) => (
      <div key={i} className="art-skeleton" style={{ height: 13, width: `${w}%`, marginBottom: 10 }} />
    ))}
    <div style={{ height: 10 }} />
    <div className="art-skeleton" style={{ height: 115, borderRadius: 10, marginBottom: 16 }} />
    {[84, 70, 78, 55].map((w, i) => (
      <div key={i} className="art-skeleton" style={{ height: 13, width: `${w}%`, marginBottom: 10 }} />
    ))}
  </div>
);


type ContentType = 'html' | 'html_url' | 'pdf' | 'csv' | 'json' | 'markdown' | 'office' | 'image' | 'download' | 'empty';

const OFFICE_EXTS = new Set(['xlsx', 'xls', 'xlsm', 'docx', 'doc', 'pptx', 'ppt']);
const IMAGE_EXTS = new Set(['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'bmp']);

function detectContentType(url: string, filename: string): ContentType {
  if (!url) return 'empty';
  const ext = filename.split('.').pop()?.toLowerCase() ?? '';
  if (ext === 'pdf') return 'pdf';
  if (ext === 'html' || ext === 'htm') return 'html_url';
  if (ext === 'csv' || ext === 'tsv') return 'csv';
  if (ext === 'json') return 'json';
  if (ext === 'md' || ext === 'txt') return 'markdown';
  if (OFFICE_EXTS.has(ext)) return 'office';
  if (IMAGE_EXTS.has(ext)) return 'image';
  return 'download';
}


const CSV_PREVIEW_ROWS = 500;

function parseCsv(text: string): string[][] {
  return text
    .split(/\r?\n/)
    .filter(l => l.trim())
    .map(line => {
      const cells: string[] = [];
      let cur = '';
      let inQ = false;
      for (let i = 0; i < line.length; i++) {
        const ch = line[i];
        if (ch === '"') {
          if (inQ && line[i + 1] === '"') { cur += '"'; i++; }
          else { inQ = !inQ; }
        } else if (ch === ',' && !inQ) {
          cells.push(cur); cur = '';
        } else {
          cur += ch;
        }
      }
      cells.push(cur);
      return cells;
    });
}

const CsvViewer: React.FC<{ guid: string; filename: string; downloadUrl: string }> = ({
  guid, filename, downloadUrl,
}) => {
  const [rows, setRows]       = useState<string[][] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(false);
  const [totalRows, setTotalRows] = useState(0);

  useEffect(() => {
    setLoading(true);
    setError(false);
    fetch(`${process.env.REACT_APP_MyAMP_Url}/api/v1/artifacts/${guid}/content`, { headers: authHeaders() })
      .then(r => r.ok ? r.text() : Promise.reject())
      .then(text => {
        const parsed = parseCsv(text);
        setTotalRows(Math.max(0, parsed.length - 1)); 
        setRows(parsed.slice(0, CSV_PREVIEW_ROWS + 1));
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [guid]);

  if (loading) return (
    <div style={{ padding: '20px 24px' }}>
      <DocLoadingSkeleton />
    </div>
  );

  if (error || !rows || rows.length === 0) return (
    <div className="art-viewer__doc-empty" style={{ flexDirection: 'column', gap: 16 }}>
      <FiFileText size={40} style={{ opacity: 0.4 }} />
      <span style={{ fontSize: 13, opacity: 0.6 }}>Could not load CSV preview.</span>
      <button className="art-viewer__embed-btn" style={{ padding: '8px 18px', fontSize: 13 }} onClick={() => triggerDownload(downloadUrl, filename)}>
        <FiDownload size={13} /> Download
      </button>
    </div>
  );

  const [header, ...dataRows] = rows;
  return (
    <div className="art-csv">
      <div className="art-csv__toolbar">
        <span className="art-csv__info">
          {totalRows.toLocaleString()} row{totalRows !== 1 ? 's' : ''} · {header.length} column{header.length !== 1 ? 's' : ''}
          {totalRows > CSV_PREVIEW_ROWS && <span style={{ opacity: 0.6 }}> (showing first {CSV_PREVIEW_ROWS})</span>}
        </span>
      </div>
      <div className="art-csv__scroll">
        <table className="art-csv__table">
          <thead>
            <tr>{header.map((h, i) => <th key={i}>{h || `Col ${i + 1}`}</th>)}</tr>
          </thead>
          <tbody>
            {dataRows.map((row, ri) => (
              <tr key={ri}>
                {header.map((_, ci) => <td key={ci}>{row[ci] ?? ''}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};


const JsonViewer: React.FC<{ guid: string; filename: string; downloadUrl: string }> = ({
  guid, filename, downloadUrl,
}) => {
  const [pretty, setPretty]   = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(false);

  useEffect(() => {
    setLoading(true);
    setError(false);
    fetch(`${process.env.REACT_APP_MyAMP_Url}/api/v1/artifacts/${guid}/content`, { headers: authHeaders() })
      .then(r => r.ok ? r.text() : Promise.reject())
      .then(text => setPretty(JSON.stringify(JSON.parse(text), null, 2)))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [guid]);

  if (loading) return (
    <div style={{ padding: '20px 24px' }}>
      <DocLoadingSkeleton />
    </div>
  );

  if (error || pretty == null) return (
    <div className="art-viewer__doc-empty" style={{ flexDirection: 'column', gap: 16 }}>
      <FiFileText size={40} style={{ opacity: 0.4 }} />
      <span style={{ fontSize: 13, opacity: 0.6 }}>Could not load JSON preview.</span>
      <button className="art-viewer__embed-btn" style={{ padding: '8px 18px', fontSize: 13 }} onClick={() => triggerDownload(downloadUrl, filename)}>
        <FiDownload size={13} /> Download
      </button>
    </div>
  );

  return (
    <div className="art-viewer__doc-content">
      <pre><code>{pretty}</code></pre>
    </div>
  );
};


const HtmlViewer: React.FC<{ guid: string; downloadUrl: string }> = ({ guid, downloadUrl }) => {
  const [html, setHtml]       = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(false);

  useEffect(() => {
    setLoading(true);
    setError(false);
    fetch(`${process.env.REACT_APP_MyAMP_Url}/api/v1/artifacts/${guid}/content`, { headers: authHeaders() })
      .then(r => r.ok ? r.text() : Promise.reject())
      .then(text => setHtml(text))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [guid]);

  if (loading) return (
    <div style={{ padding: '20px 24px' }}>
      <DocLoadingSkeleton />
    </div>
  );
  if (error) return (
    <div className="art-viewer__doc-empty" style={{ flexDirection: 'column', gap: 16 }}>
      <FiFileText size={40} style={{ opacity: 0.4 }} />
      <span style={{ fontSize: 13, opacity: 0.6 }}>Could not load HTML preview.</span>
      <button className="art-viewer__embed-btn" style={{ padding: '8px 18px', fontSize: 13 }} onClick={() => triggerDownload(downloadUrl, downloadUrl.split('/').pop()?.split('?')[0] ?? 'file')}>
        <FiDownload size={13} /> Download
      </button>
    </div>
  );

  return (
    <div className="art-viewer__embed">
      {}
      <iframe
        className="art-viewer__embed-frame"
        srcDoc={html ?? ''}
        title="HTML Preview"
        sandbox="allow-same-origin allow-scripts"
      />
    </div>
  );
};


const FileViewer: React.FC<{ guid: string; url: string; filename: string; downloadUrl: string }> = ({
  guid, url, filename, downloadUrl,
}) => {
  const type = detectContentType(url, filename);
  const [mdContent, setMdContent] = useState<string | null>(null);
  const [mdLoading, setMdLoading] = useState(false);

  useEffect(() => {
    if (type !== 'markdown') return;
    setMdLoading(true);
    fetch(`${process.env.REACT_APP_MyAMP_Url}/api/v1/artifacts/${guid}/content`, { headers: authHeaders() })
      .then(r => r.text())
      .then(t => setMdContent(t))
      .catch(() => setMdContent(null))
      .finally(() => setMdLoading(false));
  }, [guid, type]);

  if (type === 'pdf') {
    
    const gdocsUrl = `https://docs.google.com/viewer?url=${encodeURIComponent(url)}&embedded=true`;
    return (
      <div className="art-viewer__embed">
        <iframe id="art-pdf-frame" className="art-viewer__embed-frame" src={gdocsUrl} title="PDF Viewer" />
      </div>
    );
  }

  if (type === 'html_url') {
    return <HtmlViewer guid={guid} downloadUrl={downloadUrl} />;
  }

  if (type === 'markdown') {
    return (
      <div className="art-viewer__doc-content">
        {mdLoading ? (
          <DocLoadingSkeleton />
        ) : mdContent ? (
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{mdContent}</ReactMarkdown>
        ) : (
          <div className="art-viewer__doc-empty">
            <FiFileText size={28} /><span>Could not load content.</span>
          </div>
        )}
      </div>
    );
  }

  if (type === 'csv') {
    return <CsvViewer guid={guid} filename={filename} downloadUrl={downloadUrl} />;
  }

  if (type === 'json') {
    return <JsonViewer guid={guid} filename={filename} downloadUrl={downloadUrl} />;
  }

  if (type === 'image') {
    return (
      <div className="art-viewer__embed art-viewer__image-wrap">
        <img className="art-viewer__image" src={url} alt={filename} />
      </div>
    );
  }

  if (type === 'office') {
    
    const officeUrl = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(url)}`;
    const ext = filename.split('.').pop()?.toLowerCase() ?? '';
    const label = ext === 'xlsx' || ext === 'xls' || ext === 'xlsm' ? 'Excel Spreadsheet'
                : ext === 'docx' || ext === 'doc'                   ? 'Word Document'
                : 'PowerPoint';
    return (
      <div className="art-viewer__embed">
        <iframe
          className="art-viewer__embed-frame"
          src={officeUrl}
          title={label}
          frameBorder="0"
        />
      </div>
    );
  }

  
  return (
    <div className="art-viewer__doc-empty" style={{ flexDirection: 'column', gap: 16 }}>
      <FiFileText size={40} style={{ opacity: 0.4 }} />
      <span style={{ fontSize: 13, opacity: 0.6 }}>{filename}</span>
      <div style={{ display: 'flex', gap: 10 }}>
        <button className="art-viewer__embed-btn" style={{ padding: '8px 18px', fontSize: 13 }} onClick={() => triggerDownload(downloadUrl, filename)}>
          <FiDownload size={13} /> Download
        </button>
        {

}
      </div>
    </div>
  );
};


const ArtifactDetail: React.FC<{
  item:     ArtefactItem;
  tab:      Tab;
  onClose:  () => void;
}> = ({ item, tab, onClose }) => {
  const navigate = useNavigate();
  const [sasUrl, setSasUrl]     = useState<string | null>(null);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState<string | null>(null);
  const [copied, setCopied]     = useState(false);
  const { setActiveArtifactId: setCtxActiveId, setVersions: setCtxVersions } = useArtifactsStats();
  const cfg = SOURCE_CFG[tab];

  
  
  
  useEffect(() => {
    setCtxActiveId(item.id);
    fetch(`${process.env.REACT_APP_MyAMP_Url}/api/v1/artifacts/${item.id}/versions`, { headers: authHeaders() })
      .then(r => r.ok ? r.json() : null)
      .then((resp: any) => {
        setCtxVersions(resp?.data?.versions ?? []);
      })
      .catch(() => setCtxVersions([]));
  }, [item.id]); 

  useEffect(() => {
    setLoading(true);
    setError(null);
    fetch(`${process.env.REACT_APP_MyAMP_Url}/api/v1/artifacts/${item.id}`, { headers: authHeaders() })
      .then(r => r.ok ? r.json() : Promise.reject(r.status))
      .then((resp: { success?: boolean; data?: { url: string; filename: string } }) => {
        const url = resp?.data?.url ?? (resp as any).url;
        if (url) setSasUrl(url);
        else throw new Error('no url');
      })
      .catch(() => setError('Could not load this file. Please try again.'))
      .finally(() => setLoading(false));
  }, [item.id]);

  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(item.app_url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, [item.app_url]);

  const handleDownload = useCallback(() => {
    if (!item.download_url) return;
    triggerDownload(item.download_url, item.filename);
  }, [item.download_url, item.filename]);

  
  // newTab: commands open in a new tab so the artifact stays open here.
  const sourceLink: { label: string; shortLabel: string; path: string; icon: React.ReactNode; newTab?: boolean } | null = (() => {
    if (item.routine_id) {
      const runLabel = item.routine_run_id ? ` — Run #${item.routine_run_id}` : '';
      return {
        label:      `Go to Routine${runLabel}`,
        shortLabel: item.routine_run_id ? `Run #${item.routine_run_id}` : 'Routine',
        path:       `/momentum-routines/${encodeURIComponent(item.routine_id)}${item.routine_run_id != null ? `?runId=${item.routine_run_id}` : ''}`,
        icon:       <FiRepeat size={13} />,
      };
    }
    if (item.task_id) {
      return {
        label:      'Go to Command',
        shortLabel: 'Command',
        path:       `/momentum-kanban?taskId=${item.task_id}`,
        icon:       <FiClipboard size={13} />,
        newTab:     true,
      };
    }
    
    
    
    const chatConversationId = item.conversation_id || item.session_id;
    if (chatConversationId) {
      return {
        label:      'Go to Chat',
        shortLabel: 'Chat',
        path:       `/chat/${chatConversationId}`,
        icon:       <FiMessageSquare size={13} />,
      };
    }
    return null;
  })();
  const openSourceLink = () => {
    if (!sourceLink) return;
    if (sourceLink.newTab) window.open(`${window.location.origin}${sourceLink.path}`, '_blank', 'noopener,noreferrer');
    else navigate(sourceLink.path);
  };

  
  const linkedTo = item.routine_run_id
    ? `Run #${item.routine_run_id}`
    : item.task_id
    ? `CMD-${item.task_id}`
    : item.routine_id
    ? `Routine ${item.routine_id}`
    : (item.conversation_id || item.session_id)
    ? 'Chat Session'
    : '—';

  return (
    <div className="art-viewer">
      <div className="art-viewer__toolbar">
        <button className="art-viewer__back" onClick={onClose}>
          <FiArrowLeft size={14} />
        </button>
        <div className="art-viewer__toolbar-icon">{cfg.icon}</div>
        <span className="art-viewer__toolbar-title">{formatFilename(item.filename)}</span>
        <div className="art-viewer__toolbar-actions">
          {sourceLink && (
            <button
              className="art-viewer__toolbar-btn art-viewer__toolbar-btn--source"
              onClick={openSourceLink}
              title={sourceLink.label}
            >
              {sourceLink.icon}
              {sourceLink.label}
              <FiArrowRight size={11} />
            </button>
          )}
          <button className="art-viewer__toolbar-btn" onClick={handleCopy}>
            {copied ? <><FiCheck size={13} /> Copied</> : <><FiCopy size={13} /> Copy link</>}
          </button>
          <button className="art-viewer__toolbar-btn" onClick={handleDownload}>
            <FiDownload size={13} /> Download
          </button>
        </div>
      </div>

      <div className="art-viewer__pane">
        <div className="art-viewer__sidebar">
          {}
          <div className="art-viewer__sidebar-header">
            <div
              className="art-viewer__sidebar-file-icon"
              style={{
                color: extColor(item.filename),
                background: `${extColor(item.filename)}18`,
                border: `1px solid ${extColor(item.filename)}32`,
              }}
            >
              <FiFileText size={26} />
            </div>
            <div className="art-viewer__sidebar-file-info">
              {}
              <span className="art-viewer__sidebar-file-title">
                {formatFilename(item.filename)}
              </span>
              {}
              <span className="art-viewer__sidebar-filename" title={item.filename}>
                {item.filename}
              </span>
              {item.filename && (
                <span
                  className="art-viewer__sidebar-ext-badge"
                  style={{
                    color: extColor(item.filename),
                    background: `${extColor(item.filename)}1a`,
                    border: `1px solid ${extColor(item.filename)}40`,
                  }}
                >
                  {fileExtension(item.filename)}
                </span>
              )}
            </div>
          </div>

          {}
          <div className="art-viewer__sidebar-meta">
            <div className="art-viewer__sidebar-section">
              <span className="art-viewer__sidebar-label">
                {cfg.icon} Source Type
              </span>
              <span
                className="art-viewer__sidebar-type-pill"
                style={{
                  color: cfg.color,
                  background: `${cfg.color}18`,
                  border: `1px solid ${cfg.color}35`,
                }}
              >
                {cfg.icon} {cfg.label}
              </span>
            </div>

            {item.created_at && (
              <div className="art-viewer__sidebar-section">
                <span className="art-viewer__sidebar-label">
                  <FiCalendar size={10} /> Created
                </span>
                <span className="art-viewer__sidebar-value">{fmtDateTime(item.created_at)}</span>
              </div>
            )}

            {linkedTo !== '—' && (
              <div className="art-viewer__sidebar-section">
                <span className="art-viewer__sidebar-label">
                  <FiShare2 size={10} /> Linked to
                </span>
                {sourceLink ? (
                  <button
                    className="art-viewer__sidebar-link"
                    onClick={openSourceLink}
                  >
                    {sourceLink.icon} {linkedTo} <FiArrowRight size={10} />
                  </button>
                ) : (
                  <span className="art-viewer__sidebar-value" style={{ wordBreak: 'break-all', fontSize: 11 }}>
                    {linkedTo}
                  </span>
                )}
              </div>
            )}

            {}
          </div>
        </div>

        <div className="art-viewer__document">
          {loading ? (
            <DocLoadingSkeleton />
          ) : error ? (
            <div className="art-viewer__doc-empty">
              <FiFileText size={28} /><span>{error}</span>
            </div>
          ) : sasUrl ? (
            <FileViewer guid={item.id} url={sasUrl} filename={item.filename} downloadUrl={item.download_url} />
          ) : null}
        </div>
      </div>
    </div>
  );
};


const ArtifactsPage: React.FC = () => {
  const { guid }  = useParams<{ guid?: string }>();
  const navigate  = useNavigate();
  const [activeTab, setActiveTab]     = useState<Tab>('tasks');
  const [search, setSearch]           = useState('');
  const [dateFrom, setDateFrom]       = useState<string>(() => presetDates('week').from);
  const [dateTo, setDateTo]           = useState<string>(() => presetDates('week').to);
  const [showDateFilter, setShowDateFilter] = useState(false);
  const [showTypeFilter, setShowTypeFilter] = useState(false);
  const [typeFilter, setTypeFilter]   = useState<Set<string>>(new Set());
  const [page, setPage]               = useState(0);

  const dateFilterRef = useRef<HTMLDivElement>(null);
  const typeFilterRef = useRef<HTMLDivElement>(null);

  
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (dateFilterRef.current && !dateFilterRef.current.contains(e.target as Node)) {
        setShowDateFilter(false);
      }
      if (typeFilterRef.current && !typeFilterRef.current.contains(e.target as Node)) {
        setShowTypeFilter(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const { setSummary: setCtxSummary, setRecentItems: setCtxRecentItems, setSummaryLoading: setCtxSummaryLoading } = useArtifactsStats();

  const [items, setItems]             = useState<ArtefactItem[]>([]);
  const [total, setTotal]             = useState(0);
  const [summary, setSummary]         = useState<Summary | null>(null);
  const [loading, setLoading]         = useState(false);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [archivingId, setArchivingId] = useState<string | null>(null);
  const [refreshKey, setRefreshKey]   = useState(0);
  const [confirmArchive, setConfirmArchive] = useState<ArtefactItem | null>(null);

  const archiveArtifact = useCallback(async (id: string) => {
    setConfirmArchive(null);
    setArchivingId(id);
    try {
      const resp = await fetch(`${process.env.REACT_APP_MyAMP_Url}/api/v1/artifacts/${id}/archive`, {
        method: 'PATCH',
        headers: authHeaders(),
      });
      if (resp.ok) {
        setItems(prev => prev.filter(i => i.id !== id));
        setTotal(prev => Math.max(0, prev - 1));
        setRefreshKey(k => k + 1);
      }
    } catch {}
    finally { setArchivingId(null); }
  }, []);

  
  const [detailItem, setDetailItem]   = useState<ArtefactItem | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailTab, setDetailTab]     = useState<Tab>('routines');

  const { setActiveArtifactId: setCtxActiveIdPage, setVersions: setCtxVersionsPage } = useArtifactsStats();
  useEffect(() => {
    if (!guid) {
      setDetailItem(null);
      setCtxActiveIdPage(null);
      setCtxVersionsPage([]);
      return;
    }
    setDetailLoading(true);
    setDetailItem(null);
    fetch(`${process.env.REACT_APP_MyAMP_Url}/api/v1/artifacts/${guid}`, { headers: authHeaders() })
      .then(r => r.ok ? r.json() : Promise.reject('fetch-failed'))
      .then((resp: any) => {
        const d = resp?.data;
        if (!d) throw new Error('no data');
        const srcMap: Record<string, Tab> = {
          routine: 'routines', task: 'tasks', chat: 'chats', upload: 'uploads',
        };
        if (d.source) setDetailTab(srcMap[d.source] ?? 'routines');
        
        setDetailItem({
          id:             d.id    ?? guid,
          filename:       d.filename ?? '',
          session_id:     d.session_id     ?? null,
          conversation_id: d.conversation_id ?? null,
          routine_id:     d.routine_id     ?? null,
          routine_run_id: d.routine_run_id ?? null,
          task_id:        d.task_id        ?? null,
          created_at:     d.created_at     ?? '',
          app_url:        d.app_url        ?? `https://portal-stage.insuranceagency.ai/artifacts/${guid}`,
          download_url:   d.download_url   ?? '',
          version_number: d.version_number ?? 1,
          has_versions:   d.has_versions   ?? (d.version_number > 1 || !!d.parent_artifact_id),
        });
      })
      .catch(() => {
        
        setDetailItem({
          id: guid, filename: '', session_id: null, conversation_id: null, routine_id: null,
          routine_run_id: null, task_id: null, created_at: '',
          app_url: '', download_url: '',
        });
      })
      .finally(() => setDetailLoading(false));
  }, [guid]);

  
  const prevTabRef = useRef(activeTab);
  useEffect(() => {
    if (prevTabRef.current !== activeTab) {
      setPage(0);
      setTypeFilter(new Set());
    } else if (search || dateFrom || dateTo) {
      setPage(0);
    }
    prevTabRef.current = activeTab;
  }, [activeTab, search, dateFrom, dateTo]);

  
  useEffect(() => {
    setSummaryLoading(true);
    setCtxSummaryLoading(true);
    fetch(`${process.env.REACT_APP_MyAMP_Url}/api/v1/artifacts/summary`, { headers: authHeaders() })
      .then(r => r.ok ? r.json() : null)
      .then((resp: { success?: boolean; data?: Summary } | null) => {
        if (resp?.data) {
          setSummary(resp.data);
          setCtxSummary(resp.data);
        }
      })
      .catch(() => {})
      .finally(() => { setSummaryLoading(false); setCtxSummaryLoading(false); });
  
  }, [refreshKey]);

  
  
  
  
  const [tabCounts, setTabCounts] = useState<Record<'tasks' | 'routines' | 'chats', number>>({
    tasks: 0, routines: 0, chats: 0,
  });
  const [tabCountsLoading, setTabCountsLoading] = useState(true);

  useEffect(() => {
    setTabCountsLoading(true);
    const params = new URLSearchParams({ limit: '1', offset: '0' });
    if (dateFrom) params.set('date_from', dateFrom);
    if (dateTo)   params.set('date_to',   dateTo);

    const flows: Array<'tasks' | 'routines' | 'chats'> = ['tasks', 'routines', 'chats'];
    Promise.all(
      flows.map(flow => {
        const flowParams = new URLSearchParams(params);
        flowParams.set('flow', TAB_FLOW[flow]);
        return fetch(`${process.env.REACT_APP_MyAMP_Url}/api/v1/artifacts?${flowParams}`, { headers: authHeaders() })
          .then(r => r.ok ? r.json() : null)
          .then((resp: { data?: { total?: number } } | null) => resp?.data?.total ?? 0)
          .catch(() => 0);
      }),
    ).then(([tasks, routines, chats]) => {
      setTabCounts({ tasks, routines, chats });
    }).finally(() => setTabCountsLoading(false));
  }, [dateFrom, dateTo, refreshKey]);

  
  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams({
      flow:   TAB_FLOW[activeTab],
      limit:  String(LIMIT),
      offset: String(page * LIMIT),
    });
    if (dateFrom) params.set('date_from', dateFrom);
    if (dateTo)   params.set('date_to',   dateTo);

    fetch(`${process.env.REACT_APP_MyAMP_Url}/api/v1/artifacts?${params}`, { headers: authHeaders() })
      .then(r => r.ok ? r.json() : null)
      .then((resp: { success?: boolean; data?: { total?: number; items?: ArtefactItem[] } } | null) => {
        const payload = resp?.data;
        if (payload?.items) {
          
          const q = search.trim().toLowerCase();
          const filtered = q
            ? payload.items.filter(i =>
                i.filename.toLowerCase().includes(q) ||
                formatFilename(i.filename).toLowerCase().includes(q),
              )
            : payload.items;
          setItems(filtered);
          setTotal(payload.total ?? 0);
          
          if (page === 0 && !q && activeTab !== 'uploads') {
            setCtxRecentItems(payload.items.slice(0, 5).map(i => ({
              id: i.id,
              filename: i.filename,
              created_at: i.created_at,
              flow: activeTab as 'tasks' | 'routines' | 'chats',
            })));
          }
        } else {
          setItems([]);
          setTotal(0);
        }
      })
      .catch(() => { setItems([]); setTotal(0); })
      .finally(() => setLoading(false));
  }, [activeTab, page, dateFrom, dateTo, search, refreshKey]);

  const handleRefresh = useCallback(() => {
    if (loading) return;
    setRefreshKey(k => k + 1);
  }, [loading]);

  
  const availableTypes = useMemo(() => {
    const seen = new Set<string>();
    items.forEach(item => {
      const ext = item.filename.split('.').pop()?.toLowerCase();
      if (ext) seen.add(ext);
    });
    return Array.from(seen).sort();
  }, [items]);

  
  const filteredItems = useMemo(() => {
    if (typeFilter.size === 0) return items;
    return items.filter(item => {
      const ext = item.filename.split('.').pop()?.toLowerCase() ?? '';
      return typeFilter.has(ext);
    });
  }, [items, typeFilter]);

  
  
  
  const activeDatePreset: DatePreset | null = useMemo(() => {
    if (!dateFrom || !dateTo) return null;
    const today = fmtDate(new Date());
    if (dateTo !== today) return null;
    if (dateFrom === today) return 'today';
    if (dateFrom === presetDates('week').from) return 'week';
    if (dateFrom === presetDates('month').from) return 'month';
    return null;
  }, [dateFrom, dateTo]);

  const applyDatePreset = useCallback((preset: DatePreset) => {
    const { from, to } = presetDates(preset);
    setDateFrom(from);
    setDateTo(to);
    setPage(0);
  }, []);

  const clearDateFilters = useCallback(() => {
    setDateFrom('');
    setDateTo('');
    setPage(0);
  }, []);

  const dateFilterSummary = useMemo(() => {
    if (activeDatePreset === 'today') return 'Today';
    if (activeDatePreset === 'week')  return 'Last Week';
    if (activeDatePreset === 'month') return 'Last Month';
    if (dateFrom && dateTo) {
      return `${new Date(dateFrom + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – ${new Date(dateTo + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
    }
    return 'All dates';
  }, [activeDatePreset, dateFrom, dateTo]);

  const totalPages = Math.max(1, Math.ceil(total / LIMIT));
  const cfg = SOURCE_CFG[activeTab];

  const tabDef: Array<{ key: Tab; label: string; icon: React.ReactNode; count: number }> = [
    { key: 'tasks',    label: 'Commands', icon: <FiClipboard size={13} />,    count: tabCounts.tasks    },
    { key: 'routines', label: 'Routines', icon: <FiRepeat size={13} />,       count: tabCounts.routines },
    { key: 'chats',    label: 'Chats',    icon: <FiMessageSquare size={13} />, count: tabCounts.chats   },
    
  ];

  
  if (guid) {
    if (detailLoading) {
      return (
        <div className="art">
          <DetailPageSkeleton />
        </div>
      );
    }
    if (detailItem) {
      return (
        <div className="art">
          <ArtifactDetail
            item={detailItem}
            tab={detailTab}
            onClose={() => navigate('/artifacts', { replace: false })}
          />
        </div>
      );
    }
  }

  return (
    <>
    <div className="art">
      {}
      <div className="art-tabs">
        <div className="art-tabs__left">
          <div className="art-search">
            <FiSearch size={13} className="art-search__icon" />
            <input
              className="art-search__input"
              type="text"
              placeholder="Search artifacts…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
          <div className="art-tabs__row">
            {tabDef.map(tab => (
              <button
                key={tab.key}
                className={`art-tab${activeTab === tab.key ? ' art-tab--active' : ''}`}
                onClick={() => setActiveTab(tab.key)}
              >
                {tab.icon} {tab.label}
                <span className="art-tab__count">
                  {tabCountsLoading ? '…' : tab.count}
                </span>
              </button>
            ))}
          </div>
        </div>
        <div className="art-tabs__actions">
          {}
          <div ref={typeFilterRef} style={{ position: 'relative' }}>
            <button
              className={`art-filter__btn${(showTypeFilter || typeFilter.size > 0) ? ' art-filter__btn--active' : ''}`}
              onClick={() => { setShowTypeFilter(v => !v); setShowDateFilter(false); }}
              title="Filter by file type"
            >
              <FiFilter size={13} />
              {typeFilter.size > 0 && <span className="art-filter__dot" />}
            </button>
            {showTypeFilter && (
              <div className="art-filter__popover" style={{ minWidth: 220 }}>
                <div className="art-filter__type-header">
                  <span className="art-filter__label">File Type</span>
                  {typeFilter.size > 0 && (
                    <button className="art-filter__clear" onClick={() => setTypeFilter(new Set())}>
                      Clear all
                    </button>
                  )}
                </div>
                {availableTypes.length === 0 ? (
                  <span style={{ fontSize: 12, color: 'var(--text-tertiary)', padding: '4px 0' }}>
                    No files on this page
                  </span>
                ) : (
                  <div className="art-filter__type-pills">
                    {availableTypes.map(ext => {
                      const active = typeFilter.has(ext);
                      const color = EXT_COLOR[ext] ?? '#8b5cf6';
                      return (
                        <button
                          key={ext}
                          className={`art-filter__type-pill${active ? ' art-filter__type-pill--active' : ''}`}
                          style={active ? {
                            color,
                            background: `${color}20`,
                            borderColor: `${color}55`,
                          } : {}}
                          onClick={() => setTypeFilter(prev => {
                            const next = new Set(prev);
                            next.has(ext) ? next.delete(ext) : next.add(ext);
                            return next;
                          })}
                        >
                          {active && <FiCheck size={9} />}
                          {ext.toUpperCase()}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>

          {}
          <div ref={dateFilterRef} style={{ position: 'relative' }}>
            <button
              className={`art-filter__btn art-filter__btn--date${(showDateFilter || dateFrom || dateTo) ? ' art-filter__btn--active' : ''}`}
              onClick={() => { setShowDateFilter(v => !v); setShowTypeFilter(false); }}
              title="Filter by date"
            >
              <FiCalendar size={12} />
              <span className="art-filter__btn-label">{dateFilterSummary}</span>
            </button>
            {showDateFilter && (
              <div className="art-filter__popover art-filter__popover--date">
                <div className="art-filter__type-header">
                  <span className="art-filter__label">Filter by date</span>
                  {(dateFrom || dateTo) && (
                    <button className="art-filter__clear" onClick={clearDateFilters}>
                      Clear
                    </button>
                  )}
                </div>
                {}
                <div className="art-filter__preset-pills">
                  {DATE_PRESETS.map(p => (
                    <button
                      key={p.key}
                      className={`art-filter__preset-pill${activeDatePreset === p.key ? ' art-filter__preset-pill--active' : ''}`}
                      onClick={() => applyDatePreset(p.key)}
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
                {}
                <div className="art-filter__custom-dates">
                  <div className="art-filter__date-row">
                    <label className="art-filter__label">From</label>
                    <input
                      className="art-filter__date"
                      type="date"
                      value={dateFrom}
                      onChange={e => { setDateFrom(e.target.value); setPage(0); }}
                    />
                  </div>
                  <div className="art-filter__date-row">
                    <label className="art-filter__label">To</label>
                    <input
                      className="art-filter__date"
                      type="date"
                      value={dateTo}
                      onChange={e => { setDateTo(e.target.value); setPage(0); }}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          <button className="mc-btn-icon" onClick={handleRefresh} disabled={loading} title="Refresh">
            <FiRefreshCw size={14} style={loading ? { animation: 'spin 1s linear infinite' } : undefined} />
          </button>
        </div>
      </div>

      {}
      {loading ? (
        <div className="art-grid">
          {Array.from({ length: 8 }).map((_, i) => <CardSkeleton key={i} />)}
        </div>
      ) : filteredItems.length === 0 ? (
        <div className="art-empty">
          <div className="art-empty__glow" />
          <div
            className="art-empty__icon-wrap"
            style={{
              color: cfg.color,
              background: `${cfg.color}12`,
              border: `1px solid ${cfg.color}28`,
              boxShadow: `0 0 0 8px ${cfg.color}06, 0 8px 28px rgba(0,0,0,0.18)`,
            }}
          >
            {EMPTY_ICON[activeTab]}
          </div>
          <div className="art-empty__content">
            <h3 className="art-empty__title">
              {search
                ? `No results for "${search}"`
                : typeFilter.size > 0
                ? `No ${Array.from(typeFilter).map(e => e.toUpperCase()).join(', ')} files`
                : `No ${cfg.label} Artifacts`}
            </h3>
            <p className="art-empty__desc">
              {search || typeFilter.size > 0
                ? `Try adjusting your filters or switching to another tab.`
                : (dateFrom || dateTo)
                ? `Nothing found in the selected date range. Try "Clear" or a wider range.`
                : EMPTY_DESCRIPTIONS[activeTab]}
            </p>
          </div>
        </div>
      ) : (
        <>
          <div className="art-grid">
            {filteredItems.map(item => (
              <div
                key={item.id}
                className="art-card"
                style={{
                  '--card-accent': extColor(item.filename),
                  '--card-preview-bg': `linear-gradient(145deg, ${extColor(item.filename)}0f 0%, ${extColor(item.filename)}05 100%)`,
                } as React.CSSProperties}
                onClick={() => navigate(`/artifacts/${item.id}`)}
              >
                <div className="art-card__preview">
                  {item.has_versions && (
                    <span className="art-card__version-ribbon">v{item.version_number}</span>
                  )}
                  <div className="art-card__preview-file">
                    <div
                      className="art-card__file-icon"
                      style={{
                        color: extColor(item.filename),
                        '--icon-bg': `${extColor(item.filename)}18`,
                        '--icon-border': `${extColor(item.filename)}30`,
                      } as React.CSSProperties}
                    >
                      <FiFileText size={24} />
                    </div>
                    <span
                      className="art-card__ext"
                      style={{
                        color: extColor(item.filename),
                        background: `${extColor(item.filename)}1a`,
                        borderColor: `${extColor(item.filename)}40`,
                      }}
                    >
                      {fileExtension(item.filename)}
                    </span>
                  </div>
                  <div className="art-card__top-actions">
                    <span
                      className="art-card__status"
                      style={{ color: '#22c55e', borderColor: '#22c55e40', background: '#22c55e12' }}
                    >
                      Done
                    </span>
                    <button
                      type="button"
                      className={`art-card__archive-btn${archivingId === item.id ? ' art-card__archive-btn--busy' : ''}`}
                      title="Archive"
                      onClick={e => { e.stopPropagation(); setConfirmArchive(item); }}
                      disabled={archivingId === item.id}
                    >
                      <FiTrash2 size={13} />
                    </button>
                  </div>
                </div>
                <div className="art-card__body">
                  <h3 className="art-card__title">{formatFilename(item.filename)}</h3>
                  <p className="art-card__subtitle">{item.filename}</p>
                  <div className="art-card__meta">
                    <span
                      className="art-card__agent-badge"
                      style={{ background: cfg.color }}
                    >
                      {cfg.initials}
                    </span>
                    <span className="art-card__agent-name">{cfg.label}</span>
                    <span className="art-card__dot" />
                    <span className="art-card__time">{timeAgo(item.created_at)}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {totalPages > 1 && (
            <div className="art-pagination">
              <button
                className="art-pagination__btn"
                disabled={page === 0}
                onClick={() => setPage(p => p - 1)}
              >
                <FiChevronLeft size={14} />
              </button>
              <span className="art-pagination__info">Page {page + 1} of {totalPages}</span>
              <button
                className="art-pagination__btn"
                disabled={page + 1 >= totalPages}
                onClick={() => setPage(p => p + 1)}
              >
                <FiChevronRight size={14} />
              </button>
            </div>
          )}
        </>
      )}
    </div>

    {}
    {confirmArchive && (
      <div className="art-confirm-overlay" onClick={() => setConfirmArchive(null)}>
        <div className="art-confirm" onClick={e => e.stopPropagation()}>
          <div className="art-confirm__icon">
            <FiTrash2 size={22} />
          </div>
          <h3 className="art-confirm__title">Archive Artifact</h3>
          <p className="art-confirm__msg">
            Are you sure you want to archive <strong>{formatFilename(confirmArchive.filename)}</strong>?
            It will be hidden from your artifacts list.
          </p>
          <div className="art-confirm__actions">
            <button
              type="button"
              className="art-confirm__btn art-confirm__btn--cancel"
              onClick={() => setConfirmArchive(null)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="art-confirm__btn art-confirm__btn--archive"
              onClick={() => archiveArtifact(confirmArchive.id)}
            >
              <FiTrash2 size={13} /> Archive
            </button>
          </div>
        </div>
      </div>
    )}
    </>
  );
};

export default ArtifactsPage;

